// MobileTopBar.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useBase } from 'context/BaseContext';
import { useNavigation } from 'context/NavigationContext';
import { useShellChrome } from 'context/ShellChromeContext';
import { useTheme } from 'context/ThemeContext';
import { useUI } from 'context/UIContext';
import { useUser } from 'context/UserContext';

import useBaseAccessLock from 'hooks/useBaseAccessLock';
import { SectionType } from 'utilities/Enums';
import { getActiveBaseKeyFromSettings } from 'utilities/baseSettingsKeys';
import { normalizeSearchQuery } from 'services/search/BaseNodeSearchService';

import ThemeToggle from 'components/themetoggle/ThemeToggle';
import DrawerButton from 'main/drawerbutton/DrawerButton';
import Icon from 'svg/Icon';

import './MobileTopBar.css';

/**
 * MobileTopBar
 *
 * In-flow phone chrome: menu toggle (or section Back via ShellGlobalLeading),
 * find field, and theme.
 *
 * @param {boolean} isDrawerOpen - Whether the mobile drawer is open.
 * @param {Function} setIsDrawerOpen - Setter for drawer open state.
 * @returns {ReactNode}
 */
const MobileTopBar = React.memo(({ isDrawerOpen, setIsDrawerOpen }) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  const { userSettings } = useUser();
  const { activeBase } = useBase();
  const { setCurrentNode, setCurrentSection } = useNavigation();
  const { setSelectedLocation } = useUI();
  const { setMobileLeadingSlotEl } = useShellChrome();

  // ----- ROUTING ----- //

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // ----- HOOKS ----- //

  const { accessLocked: baseNavigationLocked } = useBaseAccessLock();

  // ----- STATE ----- //

  const [searchFocused, setSearchFocused] = useState(false);
  const [searchDraft, setSearchDraft] = useState('');

  // ----- REFS ----- //

  const searchInputRef = useRef(null);

  // ----- MEMOS ----- //

  const preferredBaseKeyFromSettings = getActiveBaseKeyFromSettings(userSettings);
  const activeBaseKey = useMemo(() => {
    const key = activeBase?.key || activeBase?.id || preferredBaseKeyFromSettings || '';
    return String(key).trim();
  }, [activeBase?.id, activeBase?.key, preferredBaseKeyFromSettings]);

  const isSearchRoute = String(location.pathname || '').includes('/search');

  // ----- EFFECTS ----- //

  useEffect(() => {
    if (!isSearchRoute) return;
    setSearchDraft(normalizeSearchQuery(searchParams.get('q') || ''));
  }, [isSearchRoute, searchParams]);

  // Clear the leading slot on unmount so a stale node cannot hide the drawer.
  useEffect(() => () => setMobileLeadingSlotEl(null), [setMobileLeadingSlotEl]);

  // ----- HANDLERS ----- //

  /**
   * Publishes the leading slot (Back) into ShellChromeContext.
   *
   * @param {HTMLElement|null} node
   */
  const handleLeadingSlotRef = useCallback((node) => {
    setMobileLeadingSlotEl(node);
  }, [setMobileLeadingSlotEl]);

  /**
   * Submits the find field into the Search page.
   */
  const submitSearch = useCallback(() => {
    if (baseNavigationLocked) {
      setCurrentNode(null);
      setSelectedLocation(null);
      setCurrentSection(SectionType.HOME);
      if (location.pathname !== '/') navigate('/', { replace: true });
      return;
    }

    const q = normalizeSearchQuery(searchDraft);
    setCurrentNode(null);
    setSelectedLocation(null);
    setCurrentSection(SectionType.SEARCH);
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    const query = params.toString();
    navigate(query ? `/search?${query}` : '/search');
  }, [
    baseNavigationLocked,
    location.pathname,
    navigate,
    searchDraft,
    setCurrentNode,
    setCurrentSection,
    setSelectedLocation,
  ]);

  if (!activeBaseKey) return null;

  // ----- RENDER ----- //

  return (
    <header
      className="mobile-top-bar-container"
      style={{
        backgroundColor: theme.backgroundMobile || theme.background,
        borderBottomColor: theme.outlineMuted,
      }}
    >

      {/* LEADING — Back via ShellGlobalLeading replaces the drawer menu when filled */}
      <div className="mobile-top-bar-leading">
        <div
          ref={handleLeadingSlotRef}
          className="mobile-top-bar-leading-slot"
        />
        <div className="mobile-top-bar-drawer-fallback">
          <DrawerButton
            isOpen={isDrawerOpen}
            setIsOpen={setIsDrawerOpen}
            inline
          />
        </div>
      </div>

      {/* FIND */}
      <div
        className={[
          'mobile-top-bar-search',
          searchFocused ? 'is-focused' : '',
        ].filter(Boolean).join(' ')}
        style={{
          //backgroundColor: theme.backgroundMuted,
          borderColor: searchFocused ? theme.foregroundFaded : 'transparent',
        }}
      >
        <Icon
          iconKey="search"
          color={theme.foregroundMuted}
          width={18}
          height={18}
        />
        <input
          ref={searchInputRef}
          className="mobile-top-bar-search-input"
          style={{ color: theme.foreground }}
          type="search"
          value={searchDraft}
          placeholder="Search…"
          aria-label="Find this workspace"
          enterKeyHint="search"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          onBlur={() => setSearchFocused(false)}
          onChange={(event) => setSearchDraft(event.target.value)}
          onFocus={() => setSearchFocused(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              submitSearch();
            }
            if (event.key === 'Escape') {
              event.preventDefault();
              setSearchDraft('');
              searchInputRef.current?.blur?.();
            }
          }}
        />
      </div>

      {/* THEME */}
      <div className="mobile-top-bar-theme">
        <ThemeToggle />
      </div>
    </header>
  );
});

MobileTopBar.displayName = 'MobileTopBar';

export default MobileTopBar;
